/**
 * CR321: Eligibility Computed View Model
 * 
 * Deterministic computation of eligibility data from canonical_deal_json fields.
 * Used by EligibilityBoxBlock for back-surface rendering.
 */

import { formatEligibilityRange } from "@/lib/deals/formatEligibilityRange";
import { getDealValidity } from "@/lib/deals/dealValidity";
import type { Deal } from "@/lib/types/deals";

// =============================================================================
// TYPES
// =============================================================================

export interface EligibilityComputed {
  /** What to purchase / how to qualify */
  what: string | null;
  /** Where the offer is available (merchant + channel) */
  where: string | null;
  /** When the offer is valid (formatted date range) */
  when: string | null;
  /** Parsed exclusions and restrictions */
  exclusions: string[];
  /** Parsed limits from fine print */
  limits: string[];
  /** Human-readable notes (mapped from eligibility_scope_reason) */
  notes: string | null;
  /** Admin-only evidence snippets */
  proof: string[];
  /** Whether this eligibility model has any renderable content */
  hasContent: boolean;
}

// =============================================================================
// SCOPE REASON MAPPINGS
// =============================================================================

/**
 * Map technical eligibility_scope_reason values to human-readable notes.
 * "default_consumer" is excluded as it's a technical default, not user-facing info.
 */
const SCOPE_REASON_MAP: Record<string, string | null> = {
  default_consumer: null, // Do not show
  new_customer: "Available to new customers only",
  existing_customer: "For existing customers",
  loyalty_member: "Loyalty program members only",
  credit_card_holder: "Requires specific credit card",
  first_purchase: "First purchase only",
  app_exclusive: "Available in-app only",
  email_subscriber: "Email subscribers only",
};

// =============================================================================
// LIMIT PATTERNS
// =============================================================================

/**
 * Patterns to extract limit information from fine print.
 */
const LIMIT_PATTERNS: Array<{ pattern: RegExp; format: (match: RegExpMatchArray) => string }> = [
  // CR418: Minimum spend on target category (e.g., "Minimum $30 flower spend")
  {
    pattern: /minimum\s+\$(\d+(?:\.\d+)?)\s+(\w[\w\s]*?)\s+spend/i,
    format: (m) => `Minimum $${m[1]} ${m[2].trim()} spend`
  },
  {
    pattern: /min(?:imum)?\s+\$(\d+(?:\.\d+)?)\s+spend\s+on\s+(\w[\w\s]*?)(?:\s+required|\.|,|$)/i,
    format: (m) => `Minimum $${m[1]} spend on ${m[2].trim()}`
  },
  // Dollar limits
  { 
    pattern: /\$(\d+(?:,\d{3})*(?:\.\d{2})?)\s*limit/i,
    format: (m) => `$${m[1]} limit`
  },
  // While supplies last
  { 
    pattern: /while\s+supplies?\s+last/i,
    format: () => "While supplies last"
  },
  // In-store only
  { 
    pattern: /in[- ]store\s+only/i,
    format: () => "In-store only"
  },
  // Online only
  { 
    pattern: /online\s+only/i,
    format: () => "Online only"
  },
  // Limit quantities
  { 
    pattern: /limit(?:ed)?\s+(?:to\s+)?(\d+)\s+(?:per|each)/i,
    format: (m) => `Limit ${m[1]} per customer`
  },
  { 
    pattern: /limit\s+(?:of\s+)?(\d+)/i,
    format: (m) => `Limit ${m[1]}`
  },
  // Availability may differ
  { 
    pattern: /availability\s+(?:on\s+\S+\s+)?may\s+differ/i,
    format: () => "Availability may vary"
  },
  // Participating locations
  { 
    pattern: /participating\s+(?:stores?|locations?|retailers?)/i,
    format: () => "At participating locations"
  },
];

// =============================================================================
// EXCLUSION PATTERNS
// =============================================================================

/**
 * Patterns to extract exclusions from fine print.
 */
const EXCLUSION_PATTERNS: RegExp[] = [
  /not\s+valid\s+(?:on|with|for)\s+([^.]+)/i,
  /cannot\s+be\s+combined\s+with\s+([^.]+)/i,
  /excludes?\s+([^.]+)/i,
  /(?:does|do)\s+not\s+(?:apply|include)\s+(?:to\s+)?([^.]+)/i,
];

// =============================================================================
// WHAT PATTERNS
// =============================================================================

/**
 * Patterns to extract "what to purchase" from fine print.
 */
const WHAT_PATTERNS: RegExp[] = [
  /to\s+qualify\s+(?:for\s+)?(?:this\s+)?(?:promotion\s*,?\s*)?(?:customers?\s+)?must\s+purchase\s+(?:a\s+)?([^.]+)/i,
  /must\s+purchase\s+(?:a\s+)?([^.]+)/i,
  /purchase\s+(?:a\s+)?([^.]+)\s+gift\s+card/i,
  /(?:buy|purchase)\s+(?:any\s+)?([^.]+)\s+(?:and|to)/i,
];

// =============================================================================
// CHANNEL DETECTION
// =============================================================================

interface ChannelInfo {
  inStore: boolean;
  online: boolean;
  onlineDomain?: string;
}

function detectChannel(finePrint: string | null, merchantName: string | null): ChannelInfo {
  const result: ChannelInfo = { inStore: false, online: false };
  
  if (!finePrint) return result;
  
  const text = finePrint.toLowerCase();
  
  // Detect in-store
  if (text.includes("in-store") || text.includes("in store") || text.includes("locations")) {
    result.inStore = true;
  }
  
  // Detect online - look for domains
  const domainMatch = finePrint.match(/(\w+\.(ca|com))/i);
  if (domainMatch) {
    result.online = true;
    result.onlineDomain = domainMatch[1];
  }
  
  // If neither detected but merchant mentioned, assume in-store
  if (!result.inStore && !result.online && merchantName) {
    result.inStore = true;
  }
  
  return result;
}

// =============================================================================
// MAIN BUILDER
// =============================================================================

/**
 * Build computed eligibility model from a deal's canonical fields.
 * 
 * Supports both direct Deal properties and extended canonical_deal_json fields
 * that may be merged into the deal object at runtime.
 */
export function buildEligibilityComputed(deal: Deal): EligibilityComputed {
  // Cast to any for extended field access (these come from canonical_deal_json merge)
  const extDeal = deal as Deal & {
    merchant_name?: string;
    dates?: { valid_from?: string; valid_until?: string };
    valid_from?: string;
    valid_until?: string;
    eligibility_scope_reason?: string;
    eligibility_scope_evidence_snippets?: string[];
  };
  
  // Extract canonical fields with fallbacks
  const merchantName = deal.promo_merchant_name || extDeal.merchant_name || deal.merchant || null;
  const qualifyingProduct = deal.qualifying_product_name || null;
  
  // CR432: Use unified validity helper for date resolution
  const dealValidity = getDealValidity(deal);
  const validFrom = dealValidity.validFrom;
  const validUntil = dealValidity.validTo;
  
  const finePrint = deal.fine_print || null;
  const rawExclusions = deal.exclusions || null;
  
  // Extended fields from canonical_deal_json
  const scopeReason = extDeal.eligibility_scope_reason || null;
  const scopeSnippets = extDeal.eligibility_scope_evidence_snippets || [];
  
  // Build WHAT
  let what: string | null = null;
  if (finePrint) {
    for (const pattern of WHAT_PATTERNS) {
      const match = finePrint.match(pattern);
      if (match && match[1]) {
        what = cleanExtract(match[1]);
        break;
      }
    }
  }
  // Fallback to qualifying_product_name
  if (!what && qualifyingProduct) {
    what = `${qualifyingProduct} gift card`;
  }
  
  // Build WHERE
  let where: string | null = null;
  if (merchantName) {
    const channel = detectChannel(finePrint, merchantName);
    const parts: string[] = [capitalize(merchantName)];
    
    if (channel.inStore && channel.online) {
      parts.push(`(in-store + ${channel.onlineDomain || "online"})`);
    } else if (channel.inStore) {
      parts.push("(in-store)");
    } else if (channel.online) {
      parts.push(`(${channel.onlineDomain || "online"})`);
    }
    
    where = parts.join(" ");
  }
  
  // Build WHEN
  const when = formatEligibilityRange(validFrom, validUntil);
  
  // Build EXCLUSIONS
  const exclusions: string[] = [];
  
  // From explicit exclusions field
  if (rawExclusions) {
    if (typeof rawExclusions === "string") {
      exclusions.push(rawExclusions);
    }
  }
  
  // Parse from fine print
  if (finePrint) {
    for (const pattern of EXCLUSION_PATTERNS) {
      const match = finePrint.match(pattern);
      if (match && match[1]) {
        const extracted = cleanExtract(match[1]);
        if (extracted && !exclusions.some(e => e.toLowerCase().includes(extracted.toLowerCase()))) {
          exclusions.push(extracted);
        }
      }
    }
  }
  
  // Build LIMITS
  const limits: string[] = [];
  if (finePrint) {
    for (const { pattern, format } of LIMIT_PATTERNS) {
      const match = finePrint.match(pattern);
      if (match) {
        const formatted = format(match);
        if (!limits.includes(formatted)) {
          limits.push(formatted);
        }
      }
    }
  }
  
  // Build NOTES from scope reason
  let notes: string | null = null;
  if (scopeReason && SCOPE_REASON_MAP[scopeReason] !== undefined) {
    notes = SCOPE_REASON_MAP[scopeReason];
  }
  
  // Build PROOF (admin only)
  const proof: string[] = Array.isArray(scopeSnippets) 
    ? scopeSnippets.filter((s): s is string => typeof s === "string" && s.trim().length > 0)
    : [];
  
  // Determine if we have any content to render
  const hasContent = Boolean(
    what || where || when || exclusions.length > 0 || limits.length > 0 || notes
  );
  
  return {
    what,
    where,
    when,
    exclusions,
    limits,
    notes,
    proof,
    hasContent,
  };
}

// =============================================================================
// HELPERS
// =============================================================================

function cleanExtract(text: string): string {
  // Trim, remove trailing punctuation, limit length
  let cleaned = text.trim();
  cleaned = cleaned.replace(/[.,;:!?]+$/, "");
  // Truncate at reasonable length (80 chars)
  if (cleaned.length > 80) {
    cleaned = cleaned.substring(0, 77) + "...";
  }
  return cleaned;
}

function capitalize(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}
